import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/lib/queryKeys';
import {
  fetchBorrowIssues, fetchBorrowIssue, fetchBorrowSummary, fetchBorrowReturns, fetchBorrowPayments,
  fetchBorrowStock, fetchBorrowHospitals, createBorrowIssue, recordBorrowReturn, recordBorrowPayment,
  changeBorrowSettlement, cancelBorrowIssue,
  fetchBorrowSettings, fetchBorrowSettingsAdmin, updateBorrowSettings,
} from './api';
import type { BorrowFilters, BorrowPaymentInput, BorrowReturnInput, BorrowSettingsInput, SettlementType } from './types';

/**
 * Data hooks for Medicine Borrow & Return. All branch-scoped (see
 * keys.pharmacy.borrow*), so each appends branchVersion to refetch in place on
 * a branch switch, like every other branch-scoped hook.
 */

export function useBorrowIssues(filters: BorrowFilters, enabled = true) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.borrowList({ ...filters }), branchVersion],
    queryFn: () => fetchBorrowIssues(filters),
    enabled,
  });
}

export function useBorrowIssue(id: number | null) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.borrowDetail(id ?? 0), branchVersion],
    queryFn: () => fetchBorrowIssue(id as number),
    enabled: id != null,
  });
}

export function useBorrowSummary() {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.borrowSummary(), branchVersion],
    queryFn: fetchBorrowSummary,
  });
}

export function useBorrowReturns(filters: BorrowFilters, enabled = true) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.borrowReturns({ ...filters }), branchVersion],
    queryFn: () => fetchBorrowReturns(filters),
    enabled,
  });
}

export function useBorrowPayments(filters: BorrowFilters, enabled = true) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.borrowPayments({ ...filters }), branchVersion],
    queryFn: () => fetchBorrowPayments(filters),
    enabled,
  });
}

/** Debouncing is the caller's business. */
export function useBorrowStock(search: string) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.borrowStock(search), branchVersion],
    queryFn: () => fetchBorrowStock(search),
  });
}

export function useBorrowHospitals(search: string) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.borrowHospitals(search), branchVersion],
    queryFn: () => fetchBorrowHospitals(search),
  });
}

/**
 * Every borrow mutation moves stock (issue / return / cancel) or money
 * (payment), so each refreshes the whole borrow family plus the inventory
 * and stock-adjustment lists the movement also landed in.
 */
function useInvalidateBorrow() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: keys.pharmacy.borrowAll(), exact: false });
    queryClient.invalidateQueries({ queryKey: keys.pharmacy.inventory(), exact: false });
    queryClient.invalidateQueries({ queryKey: keys.pharmacy.stockAdjustments(), exact: false });
  };
}

export function useCreateBorrowIssue() {
  const invalidate = useInvalidateBorrow();
  return useMutation({ mutationFn: createBorrowIssue, onSuccess: invalidate });
}

export function useRecordBorrowReturn() {
  const invalidate = useInvalidateBorrow();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: BorrowReturnInput }) => recordBorrowReturn(id, input),
    onSuccess: invalidate,
  });
}

export function useRecordBorrowPayment() {
  const invalidate = useInvalidateBorrow();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: BorrowPaymentInput }) => recordBorrowPayment(id, input),
    onSuccess: invalidate,
  });
}

export function useChangeBorrowSettlement() {
  const invalidate = useInvalidateBorrow();
  return useMutation({
    mutationFn: ({ id, settlement_type, item_value }: { id: number; settlement_type: SettlementType; item_value?: number }) =>
      changeBorrowSettlement(id, { settlement_type, item_value }),
    onSuccess: invalidate,
  });
}

export function useCancelBorrowIssue() {
  const invalidate = useInvalidateBorrow();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => cancelBorrowIssue(id, reason),
    onSuccess: invalidate,
  });
}

// ── Policy ───────────────────────────────────────────────────────

/** The lending policy as the Borrow screen sees it — tenant-wide, so no branchVersion. */
export function useBorrowSettings() {
  return useQuery({ queryKey: keys.borrowSettings(), queryFn: fetchBorrowSettings });
}

export function useBorrowSettingsAdmin() {
  return useQuery({ queryKey: keys.borrowSettingsAdmin(), queryFn: fetchBorrowSettingsAdmin });
}

/** Refreshes both reads (the prefix covers the admin key), so an open Borrow screen follows the new policy. */
export function useUpdateBorrowSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BorrowSettingsInput) => updateBorrowSettings(input),
    onSuccess: (settings) => {
      queryClient.setQueryData(keys.borrowSettingsAdmin(), settings);
      queryClient.invalidateQueries({ queryKey: keys.borrowSettings(), exact: false });
    },
  });
}
